import { useTranslation } from 'react-i18next';
import { rd, type RemoteData } from '@passionware/monads';
import { ArrowUpRight } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from 'v5/design-system/HoverCard';
import { Skeleton } from 'v5/design-system/Skeleton';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { focusRings } from 'v5/design-system/_common/focusRing';
import { cn } from 'v5/platform/dom/cn';
import {
  EntityCardHeader,
  type EntityCardHeaderData
} from './EntityCardHeader';
import { EntityAvatar } from './EntityOption';

/** One line of what is known about the entity: its icon, its text, where it leads. */
export type EntityCardFact = {
  icon: ReactNode;
  text: string;
  /** `mailto:`, `tel:`, a site; `null` for plain text. */
  href: string | null;
  /** More of the same kind not shown (other emails): “+N more” on this line. */
  more: number;
};

/** A tag-like mark on the entity (a group, a tag). */
export type EntityCardChip = { label: string; colour: string | null };

/** An entity as its card shows it. */
export type EntityCardData = EntityCardHeaderData & {
  /** The entity it belongs to (an organisation); `null` for none. */
  partOf: { name: string; pictureUrl: string | null } | null;
  facts: ReadonlyArray<EntityCardFact>;
  chips: ReadonlyArray<EntityCardChip>;
};

/**
 * A white card over a reference to an entity, on hovering it: its picture,
 * name and kind over a cover from the picture, the few facts worth a glance, and a way to
 * open it. At least as wide as the trigger, from its start edge. The trigger
 * keeps its own click and keyboard path.
 */
export function EntityHoverCard({
  card,
  onOpen,
  pageHref,
  media,
  children
}: {
  card: RemoteData<EntityCardData>;
  /** Opens the entity beside the page (its name). */
  onOpen(): void;
  /** The entity's own page (the corner link). */
  pageHref: string;
  /** A strip of the entity's pictures under what is known about it, when the host has them. */
  media?: ReactNode;
  /** The reference itself (a name, a link, or the field holding it). */
  children: ReactElement;
}) {
  const { t } = useTranslation('patterns');
  return (
    <HoverCard>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent className="w-[max(20rem,var(--radix-hover-card-trigger-width))] max-w-[calc(100vw-2rem)] overflow-hidden p-0">
        {rd
          .journey(rd.useLastWithPlaceholder(card))
          .wait(
            <div className="p-4">
              <EntityCardSkeleton />
            </div>
          )
          .catch(() => (
            <p className="p-4 text-sm text-fg-muted">
              {t('EntityHoverCard.cantLoad')}
            </p>
          ))
          .map(data => (
            <>
              <EntityCardHeader
                entity={data}
                name={
                  <SimpleTooltip title={t('EntityHoverCard.openInSidebar')}>
                    <button
                      type="button"
                      onClick={onOpen}
                      className={cn(
                        'truncate rounded-sm text-left text-lg font-semibold leading-tight hover:underline',
                        focusRings.focus.visible
                      )}
                    >
                      {data.name}
                    </button>
                  </SimpleTooltip>
                }
                actions={
                  <SimpleTooltip title={t('EntityHoverCard.openFullPage')}>
                    <Button asChild variant="ghost" size="sm" layout="icon">
                      <Link
                        to={pageHref}
                        aria-label={t('EntityHoverCard.openFullPage')}
                      >
                        <ArrowUpRight />
                      </Link>
                    </Button>
                  </SimpleTooltip>
                }
              />
              {(data.partOf !== null ||
                data.facts.length > 0 ||
                data.chips.length > 0) && (
                <div className="flex flex-col gap-3 px-4 pb-4 pt-3">
                  {(data.partOf !== null || data.facts.length > 0) && (
                    <ul className="flex flex-col gap-1.5 text-sm [&_svg]:size-4 [&_svg]:shrink-0">
                      {data.partOf !== null && (
                        <li className="flex min-w-0 items-center gap-2">
                          <EntityAvatar entity={data.partOf} size="2xs" />
                          <span className="truncate text-fg-default">
                            {data.partOf.name}
                          </span>
                        </li>
                      )}
                      {data.facts.map((fact, index) => (
                        <li
                          // Two facts may read the same (a phone typed twice).
                          key={`${index}:${fact.text}`}
                          className="flex min-w-0 items-center gap-2 text-fg-muted"
                        >
                          {fact.icon}
                          {fact.href === null ? (
                            <span className="truncate text-fg-default">
                              {fact.text}
                            </span>
                          ) : (
                            <a
                              href={fact.href}
                              // A website leaves the app in a tab of its own; mail and phone links don't.
                              {...(/^https?:/i.test(fact.href)
                                ? {
                                    target: '_blank',
                                    rel: 'noopener noreferrer'
                                  }
                                : {})}
                              className="truncate text-fg-default hover:underline"
                            >
                              {fact.text}
                            </a>
                          )}
                          {fact.more > 0 && (
                            <span className="shrink-0 text-xs text-fg-muted/70">
                              {t('EntityHoverCard.more', { count: fact.more })}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {data.chips.length > 0 && (
                    <ul className="flex flex-wrap gap-1">
                      {data.chips.map((chip, index) => (
                        // A group and a tag may share a name.
                        <li key={`${index}:${chip.label}`}>
                          <Badge variant="gray" tone="subtle" size="sm">
                            {chip.colour !== null && (
                              <span
                                aria-hidden
                                className="size-2 rounded-full"
                                style={{ backgroundColor: chip.colour }}
                              />
                            )}
                            {chip.label}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {media}
            </>
          ))}
      </HoverCardContent>
    </HoverCard>
  );
}

function EntityCardSkeleton() {
  return (
    <div className="flex items-center gap-3">
      <Skeleton className="size-12 rounded-md" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
  );
}
